import type { AvailabilitySlot, StaffAvailabilityRow } from "@/lib/types/staff";

type RawAvailabilityPayload = {
  masters?: Array<{
    id?: number | string | null;
    availability?: Array<{
      date?: string | null;
      slots?: Array<{
        start_sec?: number | string | null;
        end_sec?: number | string | null;
        start?: string | null;
        end?: string | null;
      }> | null;
    }> | null;
  }> | null;
};

function toSeconds(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Build date -> availability rows from a single API response payload.
 *
 * Debug note:
 * Widget calendar bugs are easiest to diagnose here first.
 * If disabled days appear enabled, inspect payload->map conversion before touching UI.
 */
export function mapAvailabilityPayloadToDates(
  payload: unknown,
  requestedDates?: string[],
  masters?: number[]
): Record<string, StaffAvailabilityRow[]> {
  const out: Record<string, StaffAvailabilityRow[]> = {};
  const dateFilter = new Set((requestedDates ?? []).filter((d) => typeof d === "string" && d.length > 0));
  const hasDateFilter = dateFilter.size > 0;
  for (const d of dateFilter) out[d] = [];

  const json = payload as RawAvailabilityPayload | null | undefined;
  if (!json || !Array.isArray(json.masters)) return out;

  const masterFilter =
    masters && masters.length > 0
      ? new Set(masters.map((id) => Number(id)).filter((id) => Number.isFinite(id)))
      : null;

  for (const m of json.masters) {
    const staffId = Number(m?.id);
    if (!Number.isFinite(staffId)) continue;
    if (masterFilter && !masterFilter.has(staffId)) continue;

    const days = Array.isArray(m?.availability) ? m.availability : [];
    for (const day of days) {
      const date = typeof day?.date === "string" ? day.date : "";
      if (!date) continue;
      if (hasDateFilter && !dateFilter.has(date)) continue;

      const slots: AvailabilitySlot[] = Array.isArray(day?.slots)
        ? day.slots.map((s) => ({
            start: toSeconds(s?.start_sec),
            end: toSeconds(s?.end_sec),
            startStr: typeof s?.start === "string" ? s.start : undefined,
            endStr: typeof s?.end === "string" ? s.end : undefined,
          }))
        : [];

      const rows = out[date] ?? (out[date] = []);
      const idx = rows.findIndex((row) => row.staffId === staffId);
      const row: StaffAvailabilityRow = { staffId, slots };
      if (idx >= 0) rows[idx] = row;
      else rows.push(row);
    }
  }

  return out;
}
